import { sanitizeHtml } from "../utils/sanitize";
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { renderCommentHtml } from '../utils/renderComment';
import ImageLightbox from './ImageLightbox';
import EmbeddedImagesPanel from './EmbeddedImagesPanel';
import { extractInlineImages, downloadInlineImage, hasUnresolvedImages } from '../utils/inlineImages';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import RichTextEditor from './RichTextEditor';
import { 
  User, Clock, MessageSquare, Send, AlertCircle, 
  CheckCircle, XCircle, Calendar, Mail, Phone, Tag,
  UserCog, Lock, Paperclip, Download, Trash2, Edit, Package, Save, X, DollarSign, Car, MapPin, Monitor,
  Timer, PlayCircle, StopCircle, ExternalLink, UserPlus, Users, ScanLine, Image as ImageIcon, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST, formatWithTimezone } from '../utils/timezone';
import { useAuth } from '../contexts/AuthContext';
import ScanAssetLookup from './ScanAssetLookup';

export default function TicketDetailDialog({ ticket, open, onOpenChange, onUpdate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [promotedImageKeys, setPromotedImageKeys] = useState([]);
  const [assets, setAssets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [boards, setBoards] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [convoSort, setConvoSort] = useState('newest');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [billingData, setBillingData] = useState({
    is_billable: false,
    billable_hours: '',
    hourly_rate: '',
    billable_amount: '',
    mileage: '',
    mileage_rate: '',
    mileage_billable: false,
    mileage_amount: ''
  });
  const [timeEntries, setTimeEntries] = useState([]);
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const fileInputRef = useRef(null);
  const [ccContacts, setCcContacts] = useState([]);
  const [ccEmail, setCcEmail] = useState('');
  const [ccName, setCcName] = useState('');
  const [ccAdding, setCcAdding] = useState(false);
  const [linkedAssets, setLinkedAssets] = useState([]);

  useEffect(() => {
    if (open && ticket) {
      loadComments();
      loadAttachments();
      loadAssets();
      loadCustomers();
      loadBoards();
      loadTechnicians();
      loadTimeEntries();
      loadCcContacts();
      loadLinkedAssets();
      setPromotedImageKeys(ticket.promoted_image_keys || []);
      setEditData({
        title: ticket.title,
        description: ticket.description || '',
        customer_id: ticket.customer_id,
        contact_person: ticket.contact_person || '',
        contact_email: ticket.contact_email || '',
        contact_phone: ticket.contact_phone || '',
        category: ticket.category || 'other',
        asset_id: ticket.asset_id || '',
        support_type: ticket.support_type || 'remote',
        board_id: ticket.board_id || ''
      });
      setBillingData({
        is_billable: ticket.is_billable || false,
        billable_hours: ticket.billable_hours?.toString() || '',
        hourly_rate: ticket.hourly_rate?.toString() || '',
        billable_amount: ticket.billable_amount?.toString() || '',
        mileage: ticket.mileage?.toString() || '',
        mileage_rate: ticket.mileage_rate?.toString() || '0.67',
        mileage_billable: ticket.mileage_billable || false,
        mileage_amount: ticket.mileage_amount?.toString() || ''
      });
    }
  }, [open, ticket]);

  const loadComments = async () => {
    if (!ticket) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/tickets/${ticket.id}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const loadAttachments = async () => {
    if (!ticket) return;
    
    try {
      const response = await api.get(`/tickets/${ticket.id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to load attachments');
    }
  };

  const loadAssets = async () => {
    try {
      const response = await api.get('/assets');
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets');
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { limit: 10000 } });
      const custData = response.data;
      setCustomers(Array.isArray(custData) ? custData : custData.customers || []);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const loadBoards = async () => {
    try {
      const response = await api.get('/ticket-boards');
      setBoards(response.data.filter(b => b.is_active));
    } catch (error) {
      console.error('Failed to load boards');
    }
  };

  const loadTimeEntries = async () => {
    if (!ticket) return;
    
    try {
      const response = await api.get(`/tickets/${ticket.id}/time-entries`);
      setTimeEntries(response.data);
      
      // Check for active timer
      const active = response.data.find(e => !e.end_time && e.technician_email === user?.email);
      setActiveTimer(active || null);
    } catch (error) {
      console.error('Failed to load time entries');
    }
  };

  const loadCcContacts = async () => {
    if (!ticket) return;
    try {
      const response = await api.get(`/tickets/${ticket.id}/cc`);
      setCcContacts(response.data);
    } catch (error) {
      console.error('Failed to load CC contacts');
    }
  };

  const handleAddCc = async () => {
    if (!ccEmail.trim()) return;
    setCcAdding(true);
    try {
      await api.post(`/tickets/${ticket.id}/cc`, { email: ccEmail.trim(), name: ccName.trim() });
      toast.success('CC contact added');
      setCcEmail('');
      setCcName('');
      loadCcContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add CC contact');
    } finally {
      setCcAdding(false);
    }
  };

  const handleRemoveCc = async (email) => {
    try {
      await api.delete(`/tickets/${ticket.id}/cc/${encodeURIComponent(email)}`);
      toast.success('CC contact removed');
      loadCcContacts();
    } catch (error) {
      toast.error('Failed to remove CC contact');
    }
  };

  const loadLinkedAssets = async () => {
    if (!ticket) return;
    try {
      const response = await api.get(`/tickets/${ticket.id}/assets`);
      setLinkedAssets(response.data);
    } catch { setLinkedAssets([]); }
  };

  const handleAddLinkedAsset = async (assetId) => {
    try {
      await api.post(`/tickets/${ticket.id}/assets`, { asset_id: assetId });
      toast.success('Asset linked');
      loadLinkedAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link asset');
    }
  };

  const handleRemoveLinkedAsset = async (assetId) => {
    try {
      await api.delete(`/tickets/${ticket.id}/assets/${assetId}`);
      toast.success('Asset unlinked');
      loadLinkedAssets();
    } catch { toast.error('Failed to unlink asset'); }
  };

  const loadTechnicians = async () => {
    try {
      const response = await api.get('/users');
      const staff = response.data.filter(u => u.role === 'admin' || u.role === 'technician');
      setTechnicians(staff);
    } catch (error) {
      console.error('Failed to load technicians');
    }
  };

  const handleClockIn = async () => {
    try {
      await api.post(`/tickets/${ticket.id}/time-entries/clock-in`, {
        is_billable: billingData.is_billable
      });
      
      toast.success('Timer started');
      loadTimeEntries();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start timer');
    }
  };

  const handleClockOut = async () => {
    if (!activeTimer) return;
    
    try {
      await api.put(`/tickets/${ticket.id}/time-entries/${activeTimer.id}/clock-out`);
      
      toast.success('Timer stopped and logged');
      setActiveTimer(null);
      setElapsedTime(0);
      loadTimeEntries();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to stop timer');
    }
  };

  const handleDeleteTimeEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    
    try {
      await api.delete(`/tickets/${ticket.id}/time-entries/${entryId}`);
      toast.success('Time entry deleted');
      loadTimeEntries();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to delete time entry');
    }
  };

  // Timer effect - update elapsed time every second
  useEffect(() => {
    if (!activeTimer) {
      setElapsedTime(0);
      return;
    }

    const startTime = new Date(activeTimer.start_time);
    
    const interval = setInterval(() => {
      const now = new Date();
      const elapsed = Math.floor((now - startTime) / 1000); // seconds
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeTimer]);

  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatDuration = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };


  const handleAddComment = async (e) => {
    e.preventDefault();
    // Check for empty content (TipTap returns <p></p> for empty)
    const isEmpty = !newComment || newComment === '<p></p>' || newComment.replace(/<[^>]*>/g, '').trim() === '';
    if (isEmpty) return;

    setSubmitting(true);
    try {
      await api.post(`/tickets/${ticket.id}/comments`, {
        comment_text: newComment,
        is_internal: isInternal
      });
      
      toast.success('Comment added successfully');
      setNewComment('');
      setIsInternal(false);
      loadComments();
      
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(`/tickets/${ticket.id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded successfully');
      loadAttachments();
      if (onUpdate) onUpdate();
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachmentId, filename) => {
    try {
      const response = await api.get(`/tickets/${ticket.id}/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    
    try {
      await api.delete(`/tickets/${ticket.id}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      loadAttachments();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/tickets/${ticket.id}`, { status: newStatus });
      toast.success('Status updated successfully');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      await api.put(`/tickets/${ticket.id}`, { priority: newPriority });
      toast.success('Priority updated successfully');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const handleAssignmentChange = async (userId) => {
    try {
      const technician = technicians.find(t => t.id === userId);
      await api.put(`/tickets/${ticket.id}`, { 
        assigned_to: userId === 'unassigned' ? null : userId,
        assigned_to_name: userId === 'unassigned' ? null : technician?.full_name
      });
      toast.success('Ticket assigned successfully');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const handleBillingUpdate = async () => {
    try {
      const billingPayload = {
        is_billable: billingData.is_billable,
        billable_hours: billingData.billable_hours ? parseFloat(billingData.billable_hours) : null,
        hourly_rate: billingData.hourly_rate ? parseFloat(billingData.hourly_rate) : null,
        billable_amount: billingData.billable_amount ? parseFloat(billingData.billable_amount) : null,
        mileage: billingData.mileage ? parseFloat(billingData.mileage) : null,
        mileage_rate: billingData.mileage_rate ? parseFloat(billingData.mileage_rate) : null,
        mileage_billable: billingData.mileage_billable,
        mileage_amount: billingData.mileage_amount ? parseFloat(billingData.mileage_amount) : null
      };

      await api.put(`/tickets/${ticket.id}`, billingPayload);
      toast.success('Billing information updated');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update billing');
    }
  };

  const calculateBillableAmount = () => {
    const hours = parseFloat(billingData.billable_hours) || 0;
    const rate = parseFloat(billingData.hourly_rate) || 0;
    return (hours * rate).toFixed(2);
  };

  const calculateMileageAmount = () => {
    const miles = parseFloat(billingData.mileage) || 0;
    const rate = parseFloat(billingData.mileage_rate) || 0;
    return (miles * rate).toFixed(2);
  };

  const calculateTotalBillable = () => {
    const timeAmount = parseFloat(billingData.billable_amount) || 0;
    const mileageAmount = billingData.mileage_billable ? (parseFloat(billingData.mileage_amount) || 0) : 0;
    return (timeAmount + mileageAmount).toFixed(2);
  };

  useEffect(() => {
    if (billingData.billable_hours && billingData.hourly_rate) {
      const calculated = calculateBillableAmount();
      if (calculated !== billingData.billable_amount) {
        setBillingData(prev => ({ ...prev, billable_amount: calculated }));
      }
    }
  }, [billingData.billable_hours, billingData.hourly_rate]);

  useEffect(() => {
    if (billingData.mileage && billingData.mileage_rate) {
      const calculated = calculateMileageAmount();
      if (calculated !== billingData.mileage_amount) {
        setBillingData(prev => ({ ...prev, mileage_amount: calculated }));
      }
    }
  }, [billingData.mileage, billingData.mileage_rate]);

  const handleSaveEdit = async () => {
    try {
      // Get customer name if customer_id changed
      const customer = customers.find(c => c.id === editData.customer_id);
      const updatePayload = {
        ...editData,
        customer_name: customer?.company_name || ticket.customer_name
      };
      
      // Get asset name if asset_id is set
      if (editData.asset_id) {
        const asset = assets.find(a => a.id === editData.asset_id);
        updatePayload.asset_name = asset?.name || '';
      }

      await api.put(`/tickets/${ticket.id}/full-edit`, updatePayload);
      toast.success('Ticket updated successfully');
      setEditMode(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Open embedded content images in a lightbox (ignores placeholders/UI icons).
  const handleContentImageClick = (e) => {
    const t = e.target;
    if (t && t.tagName === 'IMG') {
      const src = t.getAttribute('src') || '';
      if (src && !src.startsWith('data:image/svg')) {
        e.preventDefault();
        e.stopPropagation();
        const idx = inlineImages.findIndex((im) => im.src === src);
        if (idx >= 0) {
          setLightbox({ images: inlineImages, index: idx });
        } else {
          setLightbox({ images: [{ src, name: t.getAttribute('alt') || 'Image' }], index: 0 });
        }
      }
    }
  };

  if (!ticket) return null;

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-purple-100 text-purple-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.open;
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertCircle className="h-4 w-4" />,
      in_progress: <Clock className="h-4 w-4" />,
      resolved: <CheckCircle className="h-4 w-4" />,
      closed: <XCircle className="h-4 w-4" />
    };
    return icons[status] || icons.open;
  };

  const isAdmin = user?.role === 'admin';
  const isAdminOrTech = user?.role === 'admin' || user?.role === 'technician';

  const bodyHtmls = [
    ticket.description_html || ticket.description || '',
    ...comments.map((c) => c.comment_text || ''),
  ];
  const inlineImages = extractInlineImages(bodyHtmls);
  const hasUnresolvedInlineImages = hasUnresolvedImages(bodyHtmls);

  const handlePromoteImage = async (key, promoted) => {
    const prev = promotedImageKeys;
    setPromotedImageKeys((k) => (promoted ? [...new Set([...k, key])] : k.filter((x) => x !== key)));
    try {
      const res = await api.post(`/tickets/${ticket.id}/embedded-images/promote`, { key, promoted });
      if (res.data?.promoted_image_keys) setPromotedImageKeys(res.data.promoted_image_keys);
    } catch (e) {
      setPromotedImageKeys(prev);
      toast.error('Could not update image');
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="co-scope co-settings ticket-ui w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto p-3 sm:p-6 bg-[#081a25] text-[#f4f8fa] border-[#193b4b]"
        data-testid="ticket-detail-dialog"
        onEscapeKeyDown={(e) => { if (lightbox) { e.preventDefault(); setLightbox(null); } }}
        onPointerDownOutside={(e) => { if (lightbox) e.preventDefault(); }}
        onInteractOutside={(e) => { if (lightbox) e.preventDefault(); }}
      >
        <DialogHeader className="space-y-2 sm:space-y-3">
          <div className="flex flex-col gap-2">
            <DialogTitle className="flex flex-col gap-1">
              <span className="text-xs font-mono text-gray-500">{ticket.ticket_number}</span>
              <span className="text-sm sm:text-xl leading-tight">{ticket.title}</span>
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 text-xs sm:text-sm"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/tickets/${ticket.id}`);
                }}
                data-testid="open-full-page-button"
              >
                <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Full Page</span>
                <span className="sm:hidden">Full</span>
              </Button>
              {isAdmin && !editMode && (
                <Button variant="outline" size="sm" className="h-8 text-xs sm:text-sm" onClick={() => setEditMode(true)} data-testid="edit-ticket-button">
                  <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  Edit
                </Button>
              )}
              {editMode && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="h-8 text-xs sm:text-sm" onClick={() => setEditMode(false)}>
                    <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span className="hidden sm:inline">Cancel</span>
                  </Button>
                  <Button size="sm" className="h-8 text-xs sm:text-sm" onClick={handleSaveEdit} data-testid="save-ticket-button">
                    <Save className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    Save
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        {hasUnresolvedInlineImages && (
          <div data-testid="unresolved-images-warning" className="flex items-start gap-2 px-3 py-2 mb-2 bg-amber-50 border border-amber-200 rounded-md text-xs sm:text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Some embedded images couldn't be loaded. An admin can re-fetch them from{' '}
              <strong>Office 365 Settings → Repair Embedded Images</strong>.
            </span>
          </div>
        )}

        <Tabs defaultValue="details" className="space-y-3 sm:space-y-4 ticket-content" onClick={handleContentImageClick}>
          <TabsList className="flex flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="details" data-testid="tab-details" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Details</TabsTrigger>
            <TabsTrigger value="conversation" data-testid="tab-conversation" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">
              <span className="hidden sm:inline">Conversation</span>
              <span className="sm:hidden">Chat</span>
              <span className="ml-1">({comments.length})</span>
            </TabsTrigger>
            <TabsTrigger value="attachments" data-testid="tab-attachments" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">
              <span className="hidden sm:inline">Attachments</span>
              <span className="sm:hidden">Files</span>
              <span className="ml-1">({attachments.length + inlineImages.length})</span>
            </TabsTrigger>
            {isAdminOrTech && <TabsTrigger value="billing" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Billing</TabsTrigger>}
            {isAdminOrTech && <TabsTrigger value="mileage" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Mileage</TabsTrigger>}
          </TabsList>

          <TabsContent value="details" className="space-y-6">
            {editMode ? (
              /* EDIT MODE */
              <div className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={editData.title}
                    onChange={(e) => setEditData({...editData, title: e.target.value})}
                    data-testid="edit-title-input"
                  />
                </div>
                
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={editData.description}
                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                    rows={4}
                    data-testid="edit-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label>Customer *</Label>
                    <Select
                      value={editData.customer_id}
                      onValueChange={(value) => setEditData({...editData, customer_id: value})}
                    >
                      <SelectTrigger data-testid="edit-customer-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.company_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Linked Assets</Label>
                    <div className="flex items-center gap-2">
                    <Select
                      value=""
                      onValueChange={(assetId) => handleAddLinkedAsset(assetId)}
                    >
                      <SelectTrigger data-testid="edit-asset-select">
                        <SelectValue placeholder="Add asset" />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        {assets.filter(a => !linkedAssets.find(la => la.asset_id === a.id)).map((asset) => (
                          <SelectItem key={asset.id} value={asset.id}>
                            {asset.name} ({asset.serial_number || 'N/A'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <ScanAssetLookup
                      onAssetFound={(asset) => handleAddLinkedAsset(asset.id)}
                      customerId={editData.customer_id || undefined}
                      customers={customers}
                      alreadyLinkedIds={linkedAssets.map(la => la.asset_id)}
                      buttonVariant="outline"
                      buttonSize="icon"
                    />
                    </div>
                    {linkedAssets.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="edit-asset-chips">
                        {linkedAssets.map((la) => (
                          <span key={la.asset_id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                            <span className={`h-1.5 w-1.5 rounded-full ${({active:'bg-emerald-500',maintenance:'bg-amber-500',inactive:'bg-gray-400',retired:'bg-red-500'})[la.status] || 'bg-gray-400'}`} />
                            <Package className="h-3 w-3" />
                            {la.asset_name}
                            <button type="button" onClick={() => handleRemoveLinkedAsset(la.asset_id)} className="hover:text-red-600" data-testid={`edit-asset-remove-${la.asset_id}`}>
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label>Contact Person</Label>
                    <Input
                      value={editData.contact_person}
                      onChange={(e) => setEditData({...editData, contact_person: e.target.value})}
                      data-testid="edit-contact-person-input"
                    />
                  </div>
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={editData.contact_email}
                      onChange={(e) => setEditData({...editData, contact_email: e.target.value})}
                      data-testid="edit-contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label>Contact Phone</Label>
                    <Input
                      value={editData.contact_phone}
                      onChange={(e) => setEditData({...editData, contact_phone: e.target.value})}
                      data-testid="edit-contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={editData.category}
                      onValueChange={(value) => setEditData({...editData, category: value})}
                    >
                      <SelectTrigger data-testid="edit-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="hardware">Hardware</SelectItem>
                        <SelectItem value="software">Software</SelectItem>
                        <SelectItem value="network">Network</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="font-semibold">Support Type</Label>
                    <Select
                      value={editData.support_type}
                      onValueChange={(value) => setEditData({...editData, support_type: value})}
                    >
                      <SelectTrigger data-testid="edit-support-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="remote">
                          <div className="flex items-center gap-2">
                            <Monitor className="h-4 w-4" />
                            <span>Remote Support</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="onsite">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>On-Site Visit</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="hybrid">Hybrid (Remote + On-Site)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Ticket Board</Label>
                    <Select
                      value={editData.board_id || undefined}
                      onValueChange={(value) => setEditData({...editData, board_id: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger data-testid="edit-board-select">
                        <SelectValue placeholder="No board (optional)" />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="none">No Board</SelectItem>
                        {boards.map(board => (
                          <SelectItem key={board.id} value={board.id}>{board.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ) : (
              /* VIEW MODE */
              <>
                {/* Ticket Header Info */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                  <Card>
                    <CardContent className="p-3 sm:p-4">
                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          <User className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
                          <span className="font-semibold">Customer:</span>
                          <span className="truncate">{ticket.customer_name || 'N/A'}</span>
                        </div>
                        {ticket.contact_person && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <UserCog className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
                            <span className="font-semibold">Contact:</span>
                            <span className="truncate">{ticket.contact_person}</span>
                          </div>
                        )}
                        {ticket.contact_email && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <Mail className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
                            <a href={`mailto:${ticket.contact_email}`} className="text-blue-600 hover:underline truncate">
                              {ticket.contact_email}
                            </a>
                          </div>
                        )}
                        {ticket.contact_phone && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
                            <a href={`tel:${ticket.contact_phone}`} className="text-blue-600 hover:underline">
                              {ticket.contact_phone}
                            </a>
                          </div>
                        )}
                        {ticket.asset_name && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <Package className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
                            <span className="font-semibold">Asset:</span>
                            <span className="truncate">{ticket.asset_name}</span>
                          </div>
                        )}
                        {linkedAssets.length > 0 && (
                          <div className="text-xs sm:text-sm space-y-1" data-testid="linked-assets-section">
                            <div className="flex items-center gap-2">
                              <Package className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
                              <span className="font-semibold">Linked Assets:</span>
                            </div>
                            <div className="flex flex-wrap gap-1 ml-5 sm:ml-6">
                              {linkedAssets.map((la) => (
                                <span key={la.asset_id} className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] sm:text-xs font-medium" data-testid={`asset-status-${la.asset_id}`}>
                                  <span className={`h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full ${({active:'bg-emerald-500',maintenance:'bg-amber-500',inactive:'bg-gray-400',retired:'bg-red-500'})[la.status] || 'bg-gray-400'}`} title={la.status} />
                                  {la.asset_name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-3 sm:p-4">
                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
                          <span className="font-semibold">Created:</span>
                          <span className="truncate">{formatWithTimezone(ticket.created_at, 'MMM d, yyyy h:mm a')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
                          <span className="font-semibold">Updated:</span>
                          <span className="truncate">{formatWithTimezone(ticket.updated_at, 'MMM d, yyyy h:mm a')}</span>
                        </div>
                        {ticket.category && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <Tag className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
                            <span className="font-semibold">Category:</span>
                            <span className="capitalize">{ticket.category}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          {ticket.support_type === 'onsite' ? (
                            <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-purple-500 shrink-0" />
                          ) : (
                            <Monitor className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 shrink-0" />
                          )}
                          <span className="font-semibold">Support:</span>
                          <Badge 
                            variant="custom" 
                            className={`text-[10px] sm:text-xs ${
                              ticket.support_type === 'onsite' 
                                ? 'bg-purple-100 text-purple-800' 
                                : ticket.support_type === 'hybrid'
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {ticket.support_type === 'onsite' ? 'On-Site' : ticket.support_type === 'remote' ? 'Remote' : 'Hybrid'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Status, Priority, Assignment, and Board Controls */}
                {isAdminOrTech && (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4">
                    <div>
                      <Label className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 block">Status</Label>
                      <Select value={ticket.status} onValueChange={handleStatusChange}>
                        <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm" data-testid="detail-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="co-scope ticket-ui">
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 block">Priority</Label>
                      <Select value={ticket.priority} onValueChange={handlePriorityChange}>
                        <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm" data-testid="detail-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="co-scope ticket-ui">
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 block">Assigned To</Label>
                      <Select 
                        value={ticket.assigned_to || 'unassigned'} 
                        onValueChange={handleAssignmentChange}
                      >
                        <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent className="co-scope ticket-ui">
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {technicians.map((tech) => (
                            <SelectItem key={tech.id} value={tech.id}>
                              {tech.full_name} ({tech.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 block">Board</Label>
                      <Select
                        value={ticket.board_id || 'none'}
                        onValueChange={async (value) => {
                          try {
                            await api.put(`/tickets/${ticket.id}`, { board_id: value === 'none' ? null : value });
                            toast.success('Ticket moved to ' + (value === 'none' ? 'no board' : boards.find(b => b.id === value)?.name || 'board'));
                            onUpdate();
                          } catch (error) {
                            toast.error('Failed to move ticket');
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm" data-testid="detail-board-select">
                          <SelectValue placeholder="No Board" />
                        </SelectTrigger>
                        <SelectContent className="co-scope ticket-ui">
                          <SelectItem value="none">No Board</SelectItem>
                          {boards.map(board => (
                            <SelectItem key={board.id} value={board.id}>{board.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* CC Contacts Section */}
                <Card data-testid="cc-contacts-section">
                  <CardContent className="p-3 sm:p-4">
                    <h3 className="font-semibold text-sm sm:text-base mb-2 sm:mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      CC Contacts
                      {ccContacts.length > 0 && (
                        <Badge variant="outline" className="text-xs">{ccContacts.length}</Badge>
                      )}
                    </h3>
                    
                    {ccContacts.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                        {ccContacts.map((cc, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-1 bg-gray-100 rounded-full px-2 sm:px-3 py-1 text-xs sm:text-sm"
                            data-testid={`cc-contact-${idx}`}
                          >
                            <Mail className="h-3 w-3 text-gray-500 shrink-0" />
                            <span className="truncate max-w-[120px] sm:max-w-[200px]">
                              {cc.name ? `${cc.name} <${cc.email}>` : cc.email}
                            </span>
                            <button
                              onClick={() => handleRemoveCc(cc.email)}
                              className="ml-1 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                              data-testid={`remove-cc-${idx}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <Input
                        type="email"
                        placeholder="Email address"
                        value={ccEmail}
                        onChange={(e) => setCcEmail(e.target.value)}
                        className="h-8 sm:h-9 text-sm"
                        data-testid="cc-email-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCc()}
                      />
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Name (optional)"
                          value={ccName}
                          onChange={(e) => setCcName(e.target.value)}
                          className="flex-1 h-8 sm:h-9 text-sm"
                          data-testid="cc-name-input"
                          onKeyDown={(e) => e.key === 'Enter' && handleAddCc()}
                        />
                        <Button
                          size="sm"
                          onClick={handleAddCc}
                          disabled={ccAdding || !ccEmail.trim()}
                          className="shrink-0 h-8 sm:h-9 text-xs sm:text-sm px-2 sm:px-3"
                          data-testid="add-cc-button"
                        >
                          <UserPlus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          {ccAdding ? '...' : 'Add'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Ticket Description */}
                {ticket.description && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Description
                        {ticket.source === 'email' && (
                          <Badge variant="outline" className="ml-2 text-xs">From Email</Badge>
                        )}
                      </h3>
                      {(ticket.description_html || ticket.description?.includes('<div') || ticket.description?.includes('<p>')) ? (
                        <div 
                          className="ticket-description text-sm text-gray-700 break-words
                            [&_p]:my-2 [&_a]:text-blue-600 [&_a]:underline [&_a]:break-all
                            [&_br]:block [&_br]:my-1
                            [&_h1]:text-xl [&_h1]:font-bold [&_h1]:my-3
                            [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:my-2
                            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2
                            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                            [&_li]:my-1
                            [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-2
                            [&_blockquote]:border-l-4 [&_blockquote]:border-blue-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-2"
                          style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(ticket.description_html || ticket.description) }}
                        />
                      ) : (
                        <div className="ticket-description text-sm text-gray-700 break-words" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderCommentHtml(ticket.description || '')) }}
                        />
                      )}
                      {ticket.email_message_id && (
                        <div className="mt-3 pt-3 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={() => {
                              onOpenChange(false);
                              navigate('/office365');
                            }}
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            View Original Email
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="conversation" className="space-y-4">
            <div className="flex items-center justify-end">
              <button className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1" onClick={() => setConvoSort(prev => prev === 'newest' ? 'oldest' : 'newest')}>
                {convoSort === 'newest' ? '↓ Newest first' : '↑ Oldest first'}
              </button>
            </div>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-500">Loading comments...</p>
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                <p>No comments yet. Start the conversation!</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {(convoSort === 'newest' ? [...comments].reverse() : comments).map((comment) => (
                  <Card 
                    key={comment.id} 
                    className={comment.is_internal ? 'border-orange-200 bg-orange-50' : ''}
                    data-testid="ticket-comment"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                            {comment.author_name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{comment.author_name}</p>
                            <p className="text-xs text-gray-500">
                              {formatInCST(comment.created_at, 'MMM d, yyyy h:mm a')}
                              {comment.author_role && (
                                <span className="ml-2 text-blue-600 capitalize">• {comment.author_role}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        {comment.is_internal && (
                          <Badge className="bg-orange-100 text-orange-800">
                            <Lock className="h-3 w-3 mr-1" />
                            Internal Note
                          </Badge>
                        )}
                      </div>
                      {/* Render comment with HTML, markdown links, and plain URLs */}
                      <div 
                        className="ticket-description text-sm text-gray-700 ml-10 prose prose-sm max-w-none break-words overflow-hidden
                          prose-p:my-1 prose-a:text-blue-600 prose-a:underline prose-headings:my-2
                          [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-2
                          [&_a]:text-blue-600 [&_a]:underline [&_a]:break-all"
                        style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderCommentHtml(comment.comment_text || '')) }}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Add Comment Form */}
            <Card>
              <CardContent className="p-4">
                <form onSubmit={handleAddComment} className="space-y-3">
                  <div>
                    <Label htmlFor="comment" className="text-sm font-semibold mb-2 block">
                      Add Comment
                    </Label>
                    <RichTextEditor
                      content={newComment}
                      onChange={setNewComment}
                      placeholder="Type your comment here... Use the toolbar for formatting."
                      minHeight="120px"
                    />
                  </div>
                  
                  {isAdminOrTech && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="internal"
                        data-testid="internal-checkbox"
                        checked={isInternal}
                        onCheckedChange={setIsInternal}
                      />
                      <Label 
                        htmlFor="internal" 
                        className="text-sm cursor-pointer flex items-center gap-1"
                      >
                        <Lock className="h-3 w-3" />
                        Internal note (only visible to staff)
                      </Label>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={submitting || !newComment || newComment === '<p></p>'}
                      data-testid="submit-comment-button"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {submitting ? 'Sending...' : 'Add Comment'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="attachments" className="space-y-4">
            {/* Upload Section */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold mb-1">Upload Attachment</h3>
                    <p className="text-sm text-gray-600">Maximum file size: 10MB</p>
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-button"
                    >
                      <Paperclip className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Choose File'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Embedded Images (extracted from email body/replies) */}
            <EmbeddedImagesPanel
              images={inlineImages}
              ticketNumber={ticket.ticket_number}
              promotedKeys={promotedImageKeys}
              onPromote={handlePromoteImage}
              customerId={ticket.customer_id}
              customerName={ticket.customer_name}
              onOpen={(i) => setLightbox({ images: inlineImages, index: i })}
            />

            {/* Attachments List */}
            {attachments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Paperclip className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                <p>No attachments yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map((attachment) => (
                  <Card key={attachment.id} data-testid="attachment-item">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                            <Paperclip className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{attachment.filename}</p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(attachment.file_size)} • Uploaded by {attachment.uploaded_by_name}
                              {' • '}{formatInCST(attachment.created_at, 'MMM d, yyyy')}
                              {attachment.source === 'email' && (
                                <Badge className="ml-2 bg-purple-100 text-purple-800">From Email</Badge>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(attachment.id, attachment.filename)}
                            data-testid="download-attachment-button"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteAttachment(attachment.id)}
                              data-testid="delete-attachment-button"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Billing Tab */}
          {isAdminOrTech && (
            <TabsContent value="billing" className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {/* Time Tracker */}
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Timer className="h-6 w-6 text-blue-600" />
                          <div>
                            <h3 className="font-semibold text-lg">Time Tracker</h3>
                            <p className="text-sm text-gray-600">
                              {activeTimer ? 'Timer running...' : 'Clock in to start tracking time'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {activeTimer && (
                            <div className="text-right">
                              <div className="text-3xl font-mono font-bold text-blue-600">
                                {formatElapsedTime(elapsedTime)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Started {formatInCST(activeTimer.start_time, 'h:mm a')} CST
                              </div>
                            </div>
                          )}
                          {activeTimer ? (
                            <Button
                              size="lg"
                              variant="destructive"
                              onClick={handleClockOut}
                              data-testid="clock-out-button"
                            >
                              <StopCircle className="h-5 w-5 mr-2" />
                              Clock Out
                            </Button>
                          ) : (
                            <Button
                              size="lg"
                              onClick={handleClockIn}
                              data-testid="clock-in-button"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <PlayCircle className="h-5 w-5 mr-2" />
                              Clock In
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Time Entries History */}
                    {timeEntries.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Time Entries ({timeEntries.length})
                        </h3>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {timeEntries.map((entry) => (
                            <Card key={entry.id} className="border-l-4 border-l-blue-500">
                              <CardContent className="p-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-semibold text-sm">{entry.technician_name}</span>
                                      {entry.is_billable && (
                                        <Badge className="bg-green-100 text-green-800 text-xs">Billable</Badge>
                                      )}
                                      {!entry.end_time && (
                                        <Badge className="bg-blue-100 text-blue-800 text-xs animate-pulse">Active</Badge>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600 space-y-1">
                                      <div>
                                        {formatInCST(entry.start_time, 'MMM d, yyyy h:mm a')}
                                        {entry.end_time && (
                                          <span> → {formatInCST(entry.end_time, 'h:mm a')}</span>
                                        )}
                                      </div>
                                      {entry.duration_hours && (
                                        <div className="font-semibold text-blue-600">
                                          Duration: {formatDuration(entry.duration_hours)}
                                        </div>
                                      )}
                                      {entry.notes && (
                                        <div className="text-gray-500 italic">{entry.notes}</div>
                                      )}
                                    </div>
                                  </div>
                                  {isAdmin && entry.end_time && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteTimeEntry(entry.id)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-700">Total Tracked Time:</span>
                            <span className="text-xl font-bold text-blue-600">
                              {formatDuration(timeEntries.reduce((sum, e) => sum + (e.duration_hours || 0), 0))}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <Separator />

                    {/* Billable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <DollarSign className="h-6 w-6 text-green-600" />
                        <div>
                          <h3 className="font-semibold">Billable Ticket</h3>
                          <p className="text-sm text-gray-600">
                            Mark this ticket as billable if not covered under contract/SLA
                          </p>
                        </div>
                      </div>
                      <Checkbox
                        checked={billingData.is_billable}
                        onCheckedChange={(checked) => setBillingData({...billingData, is_billable: checked})}
                        data-testid="billable-checkbox"
                        className="h-6 w-6"
                      />
                    </div>

                    {billingData.is_billable && (
                      <>
                        <Separator />
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <Label htmlFor="billable_hours" className="text-sm font-semibold mb-2 block">
                              Hours Worked
                            </Label>
                            <Input
                              id="billable_hours"
                              type="number"
                              step="0.25"
                              min="0"
                              placeholder="0.00"
                              value={billingData.billable_hours}
                              onChange={(e) => setBillingData({...billingData, billable_hours: e.target.value})}
                              data-testid="billable-hours-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">Enter time in hours (e.g., 2.5 for 2.5 hours)</p>
                          </div>

                          <div>
                            <Label htmlFor="hourly_rate" className="text-sm font-semibold mb-2 block">
                              Hourly Rate ($)
                            </Label>
                            <Input
                              id="hourly_rate"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={billingData.hourly_rate}
                              onChange={(e) => setBillingData({...billingData, hourly_rate: e.target.value})}
                              data-testid="hourly-rate-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">Standard rate or custom rate for this ticket</p>
                          </div>
                        </div>

                        {/* Calculated Time Amount Display */}
                        <Card className="bg-blue-50 border-blue-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-5 w-5 text-blue-600" />
                                <span className="font-semibold text-gray-700">Time Billable Amount:</span>
                              </div>
                              <span className="text-2xl font-bold text-blue-600" data-testid="billable-amount">
                                ${billingData.billable_amount || '0.00'}
                              </span>
                            </div>
                            {billingData.billable_hours && billingData.hourly_rate && (
                              <p className="text-xs text-gray-600 mt-2">
                                {billingData.billable_hours} hours × ${billingData.hourly_rate}/hour
                              </p>
                            )}
                          </CardContent>
                        </Card>

                        <div className="flex justify-end">
                          <Button onClick={handleBillingUpdate} data-testid="save-billing-button">
                            <Save className="h-4 w-4 mr-2" />
                            Save Billing Information
                          </Button>
                        </div>
                      </>
                    )}

                    {!billingData.is_billable && (
                      <div className="text-center py-8 text-gray-500">
                        <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                        <p>This ticket is not marked as billable</p>
                        <p className="text-sm mt-1">Enable the toggle above if this work should be billed</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Mileage Tab */}
          {isAdminOrTech && (
            <TabsContent value="mileage" className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {/* Mileage Tracking Header */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Car className="h-6 w-6 text-blue-600" />
                        <div>
                          <h3 className="font-semibold">Mileage Tracking</h3>
                          <p className="text-sm text-gray-600">
                            Track travel for on-site support visits
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="mileage" className="text-sm font-semibold mb-2 block">
                          Miles Traveled
                        </Label>
                        <Input
                          id="mileage"
                          type="number"
                          step="0.1"
                          min="0"
                          placeholder="0.0"
                          value={billingData.mileage}
                          onChange={(e) => setBillingData({...billingData, mileage: e.target.value})}
                          data-testid="mileage-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Round trip distance</p>
                      </div>

                      <div>
                        <Label htmlFor="mileage_rate" className="text-sm font-semibold mb-2 block">
                          Rate per Mile ($)
                        </Label>
                        <Input
                          id="mileage_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.67"
                          value={billingData.mileage_rate}
                          onChange={(e) => setBillingData({...billingData, mileage_rate: e.target.value})}
                          data-testid="mileage-rate-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">IRS standard: $0.67/mile</p>
                      </div>

                      <div className="flex items-end">
                        <div className="w-full">
                          <Label className="text-sm font-semibold mb-2 block">
                            Mileage Cost
                          </Label>
                          <div className="h-10 px-3 rounded-md border bg-gray-50 flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">
                              ${billingData.mileage_amount || '0.00'}
                            </span>
                            <Checkbox
                              checked={billingData.mileage_billable}
                              onCheckedChange={(checked) => setBillingData({...billingData, mileage_billable: checked})}
                              data-testid="mileage-billable-checkbox"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {billingData.mileage_billable ? '✓ Bill to customer' : 'Internal cost only'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {billingData.mileage && billingData.mileage_rate && (
                      <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                        <Car className="h-3 w-3 inline mr-1" />
                        {billingData.mileage} miles × ${billingData.mileage_rate}/mile = ${billingData.mileage_amount || '0.00'}
                        {billingData.mileage_billable && <span className="text-green-700 font-semibold ml-2">(Billable to customer)</span>}
                        {!billingData.mileage_billable && <span className="text-gray-500 ml-2">(Internal tracking only)</span>}
                      </div>
                    )}

                    {/* Mileage Summary */}
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Car className="h-5 w-5 text-green-600" />
                            <span className="font-semibold text-gray-700">Mileage Cost:</span>
                          </div>
                          <span className="text-2xl font-bold text-green-600" data-testid="mileage-total">
                            ${billingData.mileage_amount || '0.00'}
                          </span>
                        </div>
                        {billingData.mileage && billingData.mileage_rate && (
                          <p className="text-xs text-gray-600 mt-2">
                            {billingData.mileage} miles × ${billingData.mileage_rate}/mile
                            {billingData.mileage_billable 
                              ? <span className="text-green-700 font-semibold ml-2">• Will be billed to customer</span>
                              : <span className="text-gray-600 ml-2">• Internal tracking only (not billed)</span>
                            }
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {!billingData.mileage && (
                      <div className="text-center py-8 text-gray-500">
                        <Car className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                        <p>No mileage tracked for this ticket</p>
                        <p className="text-sm mt-1">Enter miles above if this required on-site travel</p>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <Button onClick={handleBillingUpdate} data-testid="save-mileage-button">
                        <Save className="h-4 w-4 mr-2" />
                        Save Mileage Information
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Combined Total Summary (shown below tabs when both time and mileage are billable) */}
        {isAdminOrTech && billingData.is_billable && (billingData.billable_amount > 0 || (billingData.mileage_billable && billingData.mileage_amount > 0)) && (
          <Card className="bg-purple-50 border-purple-200 mt-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-purple-600" />
                  <span className="font-semibold text-gray-700">Total Billable to Customer:</span>
                </div>
                <span className="text-3xl font-bold text-purple-600">
                  ${calculateTotalBillable()}
                </span>
              </div>
              <div className="mt-2 space-y-1">
                {billingData.billable_hours && billingData.hourly_rate && (
                  <p className="text-xs text-gray-600">
                    Time: {billingData.billable_hours} hours × ${billingData.hourly_rate}/hour = ${billingData.billable_amount || '0.00'}
                  </p>
                )}
                {billingData.mileage_billable && billingData.mileage && billingData.mileage_rate && (
                  <p className="text-xs text-gray-600">
                    Mileage: {billingData.mileage} miles × ${billingData.mileage_rate}/mile = ${billingData.mileage_amount || '0.00'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
    {lightbox && (
      <ImageLightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />
    )}
    </>
  );
}

